import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation } from "react-router-dom";
import Input from "../../components/common/Input";
import Button from "../../components/common/Button";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import { ResetPasswordFormData } from "../../schemas/auth/formData";
import { resetPasswordSchema } from "../../schemas/auth/schemas";
import { Link } from "react-router-dom";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const { resetPassword } = useAuth();

  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!email) {
      navigate("/forgot-password");
      return;
    }
    try {
      resetPassword({ email, newPassword: data.password });
      navigate("/login");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to reset password");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 shadow-lg rounded bg-white p-10">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-primary-default">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-800">
            Please enter your new password below
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md shadow-sm space-y-4">
            <Input
              label="New Password"
              type="password"
              name="password"
              value={watch("password") || ""}
              onChange={(e) => setValue("password", e.target.value, { shouldValidate: false })}
              onBlur={() => trigger("password")}
              error={errors.password}
              placeholder="Enter your new password"
            />

            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={watch("confirmPassword") || ""}
              onChange={(e) => setValue("confirmPassword", e.target.value, { shouldValidate: false })}
              onBlur={() => trigger("confirmPassword")}
              error={errors.confirmPassword}
              placeholder="Confirm your new password"
            />
          </div>

          <div>
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Reset Password
            </Button>
          </div>

          <div className="text-sm text-center">
            <Link
              to={"/login"}
              className="font-medium text-primary-default hover:text-primary-light"
            >
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
